import { Flex, Spin } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { convertUTCDateLocal } from '../../../../utils/convertionFunc';
import nodatafound from "../../../../Assets/Images/Svg/nodatafound.svg";
import { ComposedChart, ResponsiveContainer, XAxis, YAxis, Tooltip, Line } from 'recharts';
import VitalService from '../../../../Services/VitalService';

const VITAL_COLUMN_DETAILS = {
  BP: { label: "Blood Pressure", keys: ["BPS", "BPD"] },
  RR: { label: "Respiratory Rate", keys: ["RR"] },
  TP: { label: "Temperature", keys: ["TP"] },
  HR: { label: "Heart Rate", keys: ["HR"] },
  SPO2: { label: "SpO2", keys: ["SPO2"] },
  WE: { label: "Weight", keys: ["WE"] },
  BMI: { label: "BMI", keys: ["BMI"] },
  BG: { label: "Blood Glucose", keys: ["BG"] },
};

function VitalChart({ selectedVital, selectedPatient }) {

  // state
  const [savedVitalsList, setSavedVitalsList] = useState([]);
  const [savedVitLoader, setSavedVitLoader] = useState(false);
  const [tab, setTab] = useState("7D");
  // default to last 7 days inclusive (6 days ago -> today)
  const [currFromDate, setCurrFromDate] = useState(dayjs().subtract(6, 'day').format("MM/DD/YYYY"));
  const [currToDate, setCurrToDate] = useState(dayjs().format("MM/DD/YYYY"));
  const [errorMsg, setErrorMsg] = useState(null);

  // request guard to ignore stale requests
  const fetchCounterRef = useRef(0);

  // VITAL_MAP — how API vitalCode maps to our columns (include BG)
  const VITAL_MAP = {
    BMI: "BMI",
    HR: "HR",
    BPS: "BPS",
    BPD: "BPD",
    WE: "WE",
    HI: "HI",
    SPO2: "SPO2",
    TP: "TP",
    RR: "RR",
    BG: "BG",
  };

  const chartData = useMemo(() => {
    if (!Array.isArray(savedVitalsList) || savedVitalsList.length === 0) return [];

    const sorted = [...savedVitalsList].sort((a, b) => {
      const da = dayjs(a.recordedDateTime);
      const db = dayjs(b.recordedDateTime);
      return da.isBefore(db) ? -1 : da.isAfter(db) ? 1 : 0;
    });

    return sorted.map(item => {
      const dateLabel = tab === '1D'
        ? convertUTCDateLocal(item.recordedDateTime, "DD/MM HH:mm")
        : convertUTCDateLocal(item.recordedDateTime, "DD/MM");
      const point = {
        date: dateLabel,
        recordedDateTime: item.recordedDateTime,
      };
      Object.keys(VITAL_MAP).forEach(code => {
        point[code] = item[code] ?? null;
        if (item[`${code}_STATUS`] !== undefined) {
          point[`${code}_STATUS`] = item[`${code}_STATUS`];
        }
      });
      return point;
    });
  }, [savedVitalsList, tab]);

  // FETCH function: overrideDates means explicit from/to to use (local variables) — avoids stale setState issues
  const fetchPatientVitals = async (overrideDates) => {
    // increment request id
    const myReqId = ++fetchCounterRef.current;
    setErrorMsg(null);
    setSavedVitLoader(true);

    const fromDate = overrideDates?.fromDate ?? currFromDate;
    const toDate = overrideDates?.toDate ?? currToDate;

    // defensive: require patient id
    if (!selectedPatient?.patientId) {
      setSavedVitalsList([]);
      setSavedVitLoader(false);
      return;
    }

    const basePayload = {
      account_Id: selectedPatient?.account_Id,
      patientId: selectedPatient?.patientId,
      fromDate,
      toDate,
      status: 1,
    };

    try {
      const response = await VitalService.getVitalsData(basePayload);
      const summary = Array.isArray(response?.data) ? response.data : [];

      // Behavior differs by tab:
      // 1D -> keep ALL summary items for that day (so multiple detail calls for each summary entry)
      // 7D -> group by day and pick latest per day (max timestamp)
      let chosenSummary = [];

      if (tab === '1D') {
        // keep all summary items (they should all be within that single day range)
        chosenSummary = summary.slice(); // shallow copy
        // sort ascending to keep chart order consistent
        chosenSummary.sort((a, b) => dayjs(a.recordedDateTime).isBefore(dayjs(b.recordedDateTime)) ? -1 : 1);
      } else {
        // group by dayKey YYYY-MM-DD and pick latest per day
        const perDayMap = new Map();
        summary.forEach(item => {
          if (!item?.recordedDateTime) return;
          const dayKey = dayjs(item.recordedDateTime).format('YYYY-MM-DD');
          const existing = perDayMap.get(dayKey);
          if (!existing) perDayMap.set(dayKey, item);
          else {
            if (dayjs(item.recordedDateTime).isAfter(dayjs(existing.recordedDateTime))) {
              perDayMap.set(dayKey, item);
            }
          }
        });
        chosenSummary = Array.from(perDayMap.values())
          .sort((a, b) => dayjs(a.recordedDateTime).isBefore(dayjs(b.recordedDateTime)) ? -1 : 1);
      }

      // Make detail requests only for chosen summary items (no duplicates)
      const detailRequests = chosenSummary.map(item => {
        const detailPayload = {
          ...basePayload,
          fromDate: item.recordedDateTime,
          toDate: item.recordedDateTime,
        };
        return VitalService.getSpecificVitalData(detailPayload)
          .then(res => ({
            recordedDateTime: item.recordedDateTime,
            data: Array.isArray(res.data) ? res.data : []
          }))
          .catch(err => {
            console.error("getSpecificVitalData error for date", item.recordedDateTime, err);
            return { recordedDateTime: item.recordedDateTime, data: [] };
          });
      });

      const results = await Promise.all(detailRequests);

      // Map into finalVitalArray with consistent keys
      const finalVitalArray = results.map(({ recordedDateTime, data }) => {
        const vitalRow = {
          recordedDateTime,
          ...Object.fromEntries(
            Object.keys(VITAL_MAP).flatMap(code => [
              [code, null],
              [`${code}_STATUS`, null],
            ])
          ),
        };

        data.forEach(v => {
          const key = VITAL_MAP[v.vitalCode];
          if (!key) return;
          vitalRow[key] = v.value ?? null;
          vitalRow[`${key}_STATUS`] = v.vitalStatus ?? null;
        });

        return vitalRow;
      });

      // set state only if not stale
      if (fetchCounterRef.current === myReqId) {
        setSavedVitalsList(finalVitalArray);
      }
    } catch (err) {
      console.error(err);
      if (fetchCounterRef.current === myReqId) {
        setErrorMsg("Failed to load vitals. Please try again.");
        setSavedVitalsList([]);
      }
    } finally {
      if (fetchCounterRef.current === myReqId) {
        setSavedVitLoader(false);
      }
    }
  };

  // on patient change: reset to 7D default range and fetch
  useEffect(() => {
    const from = dayjs().subtract(6, 'day').format("MM/DD/YYYY");
    const to = dayjs().format("MM/DD/YYYY");
    setTab("7D");
    setCurrFromDate(from);
    setCurrToDate(to);
    fetchPatientVitals({ fromDate: from, toDate: to });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatient?.patientId]);

  // Prev/Next handlers: compute ranges locally and pass to fetch
  const handlePrev = async () => {
    if (tab === '1D') {
      const newDate = dayjs(currFromDate).subtract(1, 'day').format("MM/DD/YYYY");
      setCurrFromDate(newDate);
      setCurrToDate(newDate);
      await fetchPatientVitals({ fromDate: newDate, toDate: newDate });
    } else {
      const fromdate = dayjs(currFromDate).subtract(7, 'day').format("MM/DD/YYYY");
      const todate = dayjs(currToDate).subtract(7, 'day').format("MM/DD/YYYY");
      setCurrFromDate(fromdate);
      setCurrToDate(todate);
      await fetchPatientVitals({ fromDate: fromdate, toDate: todate });
    }
  };

  const handleNext = async () => {
    const todayEnd = dayjs().endOf('day');
    if (tab === '1D') {
      const candidate = dayjs(currFromDate).add(1, 'day');
      if (candidate.isAfter(todayEnd)) return; // don't go beyond today
      const newDate = candidate.format("MM/DD/YYYY");
      setCurrFromDate(newDate);
      setCurrToDate(newDate);
      await fetchPatientVitals({ fromDate: newDate, toDate: newDate });
    } else {
      const candidateTo = dayjs(currToDate).add(7, 'day');
      if (candidateTo.isAfter(todayEnd)) {
        // clamp so last day is today
        const todate = todayEnd.format("MM/DD/YYYY");
        const fromdate = dayjs(todate).subtract(6, 'day').format("MM/DD/YYYY");
        setCurrFromDate(fromdate);
        setCurrToDate(todate);
        await fetchPatientVitals({ fromDate: fromdate, toDate: todate });
        return;
      }
      const fromdate = dayjs(currFromDate).add(7, 'day').format("MM/DD/YYYY");
      const todate = dayjs(currToDate).add(7, 'day').format("MM/DD/YYYY");
      setCurrFromDate(fromdate);
      setCurrToDate(todate);
      await fetchPatientVitals({ fromDate: fromdate, toDate: todate });
    }
  };

  const isNextDisabled = (() => {
    const today = dayjs().endOf('day');
    return dayjs(currToDate).endOf('day').isSame(today, 'day');
  })();

  // Tab switcher
  const handleTabSwitch = async (type) => {
    const todayDate = dayjs().format("MM/DD/YYYY");
    let fromdate, todate;
    if (type === '1D') {
      fromdate = todayDate;
      todate = todayDate;
    } else {
      // 7D -> last 7 days inclusive
      fromdate = dayjs().subtract(6, 'day').format("MM/DD/YYYY");
      todate = todayDate;
    }
    setTab(type);
    setCurrFromDate(fromdate);
    setCurrToDate(todate);
    await fetchPatientVitals({ fromDate: fromdate, toDate: todate });
  };




  const renderChart = () => {
    if (!selectedVital) return null;

    const details = VITAL_COLUMN_DETAILS[selectedVital];
    if (!details || !details.keys || details.keys.length === 0) return null;

    if (savedVitLoader) {
      return (
        <div className="flex items-center justify-center h-[inherit]">
          <Flex align="center" gap="middle">
            <Spin size="large" tip="Loading" />
          </Flex>
        </div>
      );
    }

    if (errorMsg) {
      return (
        <div className="flex flex-col items-center justify-center">
          <p className="text-red-600">{errorMsg}</p>
        </div>
      );
    }

    if (chartData.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-[inherit]">
          <img src={nodatafound} alt="nodatafound" />
          <p className="font-medium text-xl">No data found!!</p>
        </div>
      );
    }

    return (
      <>
        <div className="w-full h-[inherit]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData}>
              <XAxis
                dataKey="date"
                interval={0}
                tick={{ angle: tab === '1D' ? -45 : -30, textAnchor: "end" }}
                padding={{ right: 30 }}
              />
              <YAxis padding={{ top: 30 }} />
              <Tooltip />
              {details.keys.map((key, index) => (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={index === 0 ? "#5B8FF9" : "#5AD8A6"}
                  dot={{ r: 4 }}
                  isAnimationActive={false}
                />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </>
    );
  };

  return (
    <div className='p-5'>
      <div className='flex items-center justify-between'>

        <div className="flex gap-3">
          <button
            onClick={() => handleTabSwitch('1D')}
            className={`${tab === "1D" ? 'bg-[#189FAA] text-white' : "bg-gray-200"} px-4 py-1 rounded`}
          >
            1D
          </button>
          <button
            onClick={() => handleTabSwitch('7D')}
            className={`${tab === "7D" ? 'bg-[#189FAA] text-white' : "bg-gray-200"} px-4 py-1 rounded`}
          >
            7D
          </button>
        </div>

        <div className="flex gap-4 items-center">
          <button onClick={handlePrev} className="px-3 py-2 bg-gray-300 rounded">
            ← Prev
          </button>
          <span className='text-sm font-semibold'>{dayjs(currFromDate).format('DD/MM/YYYY')+" - "+dayjs(currToDate).format('DD/MM/YYYY')}</span>
          <button
            onClick={handleNext}
            disabled={isNextDisabled}
            className={`px-3 py-2 rounded ${isNextDisabled ? 'bg-gray-200 text-gray-400' : 'bg-gray-300'}`}
          >
            Next →
          </button>
        </div>
      </div>
      <div className='h-80'>
        {renderChart()}
      </div>
    </div>
  )
}

export default VitalChart;

// import React, { useEffect, useState } from "react";
// import {
//   LineChart,
//   Line,
//   XAxis,
//   YAxis,
//   Tooltip,
//   ResponsiveContainer,
//   CartesianGrid,
//   Legend,
// } from "recharts";
// import { CloseOutlined } from "@ant-design/icons";
// import dayjs from "dayjs";
// import { Spin } from "antd";
// import VitalService from "../../../../Services/VitalService";

// const VitalChart = ({ vitalCode, closePopup, title }) => {
//   const patient = JSON.parse(sessionStorage.getItem("patientData"));
//   const patientId = patient?.patientId;

//   const [graphData, setGraphData] = useState([]);
//   const [mode, setMode] = useState("1"); // 1=1day, 7=7days
//   const [currentDate, setCurrentDate] = useState(dayjs());
//   const [loading, setLoading] = useState(false);

//   const isBP = vitalCode === "BP";

//   const fetchData = async () => {
//     setLoading(true);

//     const fetchDate = currentDate; // Prevents unexpected mutation during render

//     let fromDate =
//       mode === "1"
//         ? fetchDate.startOf("day")
//         : fetchDate.subtract(6, "day").startOf("day");

//     let toDate = fetchDate.endOf("day");

//     const payload = {
//       patientId,
//       vitalCodes: isBP ? "BPS,BPD" : vitalCode,
//       fromDate: fromDate.format("YYYY-MM-DD HH:mm:ss"),
//       toDate: toDate.format("YYYY-MM-DD HH:mm:ss"),
//     };

//     try {
//       const res = await VitalService.getAllVitalsData(payload);
//       const items = res?.data || [];

//       // Map grouped BP values OR single value vitals
//       const formatted = items.reduce((acc, x) => {
//         const dt = x.recordedDateTime;
//         let row = acc.find((item) => item.fullDateTime === dt);
//         if (!row) {
//           row = {
//             fullDateTime: dt,
//             date: dayjs(dt).format("DD/MM/YYYY"),
//           };
//           acc.push(row);
//         }
//         if (isBP) {
//           row[x.vitalCode] = x.value ?? null;
//         } else {
//           row.value = x.value ?? null;
//         }
//         return acc;
//       }, []);

//       // If 7 days → pick latest per day
//       let finalData = formatted;
//       if (mode === "7") {
//         const latest = {};
//         formatted.forEach((v) => {
//           const d = v.date;
//           if (
//             !latest[d] ||
//             dayjs(v.fullDateTime).isAfter(latest[d].fullDateTime)
//           ) {
//             latest[d] = v;
//           }
//         });
//         finalData = Object.values(latest);
//       }

//       // Sort by time ASC
//       finalData.sort((a, b) =>
//         dayjs(a.fullDateTime).isAfter(dayjs(b.fullDateTime)) ? 1 : -1
//       );

//       setGraphData(finalData);
//     } catch (err) {
//       console.error("Vitals fetch error:", err);
//     }

//     setLoading(false);
//   };

//   useEffect(() => {
//     fetchData();
//   }, [currentDate, mode, vitalCode]);

//   const handlePrev = () => {
//     setCurrentDate((prev) =>
//       mode === "1" ? prev.subtract(1, "day") : prev.subtract(7, "day")
//     );
//   };

//   const handleNext = () => {
//     setCurrentDate((prev) =>
//       mode === "1" ? prev.add(1, "day") : prev.add(7, "day")
//     );
//   };

//   const CustomTooltip = ({ active, payload, label }) => {
//     if (!active || !payload?.length) return null;
//     return (
//       <div className="p-1 bg-white border text-xs">
//         {isBP ? (
//           <>
//             {payload.map((p) => (
//               <div key={p.dataKey}>
//                 {p.dataKey}: {p.value}
//               </div>
//             ))}
//           </>
//         ) : (
//           `Value: ${payload[0].value}`
//         )}
//         <div>{label}</div>
//       </div>
//     );
//   };

//   return (
//     <>
//       <div className="justify-center items-center flex fixed inset-0 z-50">
//         <div className="relative w-11/12 md:w-1/3 mx-auto max-w-3xl">
//           <div className="rounded-lg shadow-lg bg-white border border-gray-200 p-5 h-[430px] relative">
//             {/* Header */}
//             <div className="flex justify-between mb-3">
//               <p className="text-xl font-medium">{title}</p>
//               <CloseOutlined className="cursor-pointer" onClick={closePopup} />
//             </div>

//             {/* Buttons */}
//             <div className="flex gap-2 justify-center my-2">
//               <button
//                 className={`px-3 py-1 rounded ${
//                   mode === "1" ? "bg-blue-500 text-white" : "bg-gray-200"
//                 }`}
//                 onClick={() => setMode("1")}
//               >
//                 1 Day
//               </button>
//               <button
//                 className={`px-3 py-1 rounded ${
//                   mode === "7" ? "bg-blue-500 text-white" : "bg-gray-200"
//                 }`}
//                 onClick={() => setMode("7")}
//               >
//                 7 Days
//               </button>
//             </div>

//             {/* Date Controls */}
//             <div className="flex justify-between mb-3">
//               <button
//                 className="px-3 py-1 bg-gray-200 rounded"
//                 onClick={handlePrev}
//                 disabled={loading}
//               >
//                 ←
//               </button>

//               <span>
//                 {mode === "1"
//                   ? currentDate.format("DD/MM/YYYY")
//                   : `${currentDate
//                       .subtract(6, "day")
//                       .format("DD/MM/YYYY")} - ${currentDate.format(
//                       "DD/MM/YYYY"
//                     )}`}
//               </span>

//               <button
//                 className="px-3 py-1 bg-gray-200 rounded"
//                 onClick={handleNext}
//                 disabled={loading || dayjs(currentDate).isSame(dayjs(), "day")}
//               >
//                 →
//               </button>
//             </div>

//             {/* Chart */}
//             <div className="w-full h-[260px]">
//               {loading ? (
//                 <div className="flex justify-center items-center h-full">
//                   <Spin />
//                 </div>
//               ) : graphData.length === 0 ? (
//                 <div className="flex justify-center items-center h-full text-gray-500">
//                   No data
//                 </div>
//               ) : (
//                 <ResponsiveContainer width="100%" height="100%">
//                   <LineChart data={graphData}>
//                     <CartesianGrid strokeDasharray="3 3" />
//                     <Tooltip content={<CustomTooltip />} />
//                     <Legend />
//                     {isBP ? (
//                       <>
//                         <Line
//                           type="monotone"
//                           dataKey="BPS"
//                           stroke="#3b82f6"
//                           strokeWidth={2}
//                           dot
//                         />
//                         <Line
//                           type="monotone"
//                           dataKey="BPD"
//                           stroke="#ef4444"
//                           strokeWidth={2}
//                           dot
//                         />
//                       </>
//                     ) : (
//                       <Line
//                         type="monotone"
//                         dataKey="value"
//                         stroke="#3b82f6"
//                         strokeWidth={2}
//                         dot
//                       />
//                     )}
//                     <XAxis dataKey="date" />
//                     <YAxis />
//                   </LineChart>
//                 </ResponsiveContainer>
//               )}
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Modal Overlay */}
//       <div className="opacity-25 fixed inset-0 z-40 bg-black" />
//     </>
//   );
// };

// export default VitalChart;

